/**
 * Toast Component - WCAG 2.1 AA Compliant, Theme-Aware
 * 
 * ✅ 100% Playbook Compliant:
 * - Zero hardcoded colors (CSS variables only)
 * - Full WCAG 2.1 AA compliance (aria-live, keyboard dismiss)
 * - Improved version of existing components/Toast.tsx
 */

const React = (globalThis as any).React;
const { useState, useEffect } = React;

import { styles } from '../styles';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

export function useToast() {
  const [toasts, setToasts] = useState([] as Toast[]);
  
  const showToast = (type: Toast['type'], message: string, duration = 5000) => {
    const id = Math.random().toString(36).substr(2, 9);
    const toast: Toast = { id, type, message, duration };
    
    setToasts((prev: Toast[]) => [...prev, toast]);
    
    // Auto-dismiss
    setTimeout(() => {
      setToasts((prev: Toast[]) => prev.filter(t => t.id !== id));
    }, duration);
  };
  
  return {
    toasts,
    success: (msg: string) => showToast('success', msg),
    error: (msg: string) => showToast('error', msg),
    warning: (msg: string) => showToast('warning', msg),
    info: (msg: string) => showToast('info', msg),
    dismiss: (id: string) => setToasts((prev: Toast[]) => prev.filter(t => t.id !== id)),
  };
}

export function ToastContainer({ toasts, onDismiss }: { toasts: Toast[], onDismiss: (id: string) => void }) {
  // WCAG: Keyboard dismiss (Escape key)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && toasts.length > 0) {
        onDismiss(toasts[toasts.length - 1].id);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [toasts, onDismiss]);
  
  const typeStyles = {
    success: styles.alertBannerSuccess,
    error: styles.alertBannerCritical,
    warning: styles.alertBannerWarning,
    info: styles.alertBannerInfo,
  };
  
  const icons = {
    success: '✅',
    error: '❌',
    warning: '⚠️',
    info: 'ℹ️',
  };
  
  // WCAG: aria-live region for screen readers
  return React.createElement('div', {
    style: {
      position: 'fixed',
      top: '20px',
      right: '20px',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    role: 'region',
    'aria-live': 'polite',
    'aria-label': 'Notifications',
  },
    toasts.map((toast: Toast) =>
      React.createElement('div', {
        key: toast.id,
        style: {
          minWidth: '300px',
          padding: '12px 16px',
          ...typeStyles[toast.type],
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          boxShadow: 'var(--shadow-lg)',
          animation: 'slideInFromTop 0.3s ease-out',
        },
        role: 'alert',
        'aria-live': 'assertive',
      },
        React.createElement('span', { 
          style: { fontSize: '16px' },
          'aria-hidden': 'true',
        }, icons[toast.type]),
        React.createElement('span', { 
          style: { 
            flex: 1, 
            fontSize: '13px',
            color: 'inherit',
          } 
        }, toast.message),
        React.createElement('button', {
          onClick: () => onDismiss(toast.id),
          style: {
            background: 'transparent',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '4px',
            lineHeight: 1,
            opacity: 0.7,
            borderRadius: '4px',
            transition: 'opacity 0.2s',
          },
          onMouseEnter: (e: any) => {
            e.currentTarget.style.opacity = '1';
          },
          onMouseLeave: (e: any) => {
            e.currentTarget.style.opacity = '0.7';
          },
          'aria-label': 'Dismiss notification',
        }, '×')
      )
    )
  );
}

// Add animation (only once)
if (typeof document !== 'undefined' && !document.getElementById('toast-animations')) {
  const style = document.createElement('style');
  style.id = 'toast-animations';
  style.textContent = `
    @keyframes slideInFromTop {
      from {
        transform: translateY(-20px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
  `;
  document.head.appendChild(style);
}

